import { cn } from "@au-schein-de/au-ui-library";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
	getPharmacyPickerSortOptions,
	type PharmacyPickerSort,
	type PharmacyPickerSortContext,
	resolvePharmacyPickerSort,
	usePharmacyPickerSort,
} from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";

export const PHARMACY_PICKER_SORT_PANEL_TEST_ID = "pharmacy-picker-sort-panel";
export const PHARMACY_PICKER_SORT_PANEL_WIDTH_PX = 264;

export const PHARMACY_PICKER_SORT_LABEL_KEYS = {
	az: "vendor.picker.sort.az",
	za: "vendor.picker.sort.za",
	freeFrom: "vendor.picker.sort.freeFrom",
	distance: "vendor.picker.sort.distance",
	priceAsc: "vendor.picker.sort.priceAsc",
} as const satisfies Record<PharmacyPickerSort, string>;

export const isPharmacyPickerSortPanelEvent = (event: {
	target: EventTarget | null;
	detail?: { originalEvent?: Event };
}): boolean => {
	const target = event.detail?.originalEvent?.target ?? event.target;
	return (
		target instanceof Element &&
		Boolean(
			target.closest(`[data-testid="${PHARMACY_PICKER_SORT_PANEL_TEST_ID}"]`),
		)
	);
};

export function PharmacyPickerSortOptions({
	sortContext,
	selected,
	onSelect,
	optionClassName,
}: {
	sortContext: PharmacyPickerSortContext;
	/** Highlighted option — the applied sort (desktop) or an in-progress draft (mobile). */
	selected: PharmacyPickerSort;
	onSelect: (sort: PharmacyPickerSort) => void;
	/** The mobile sheet sits on gray-100, so its rows go white (Figma 4659:59073). */
	optionClassName?: string;
}) {
	const { t } = useTranslation("product");
	return (
		<>
			{getPharmacyPickerSortOptions(sortContext).map((option) => (
				<button
					key={option}
					type="button"
					className={cn(
						"w-full cursor-pointer rounded-xl p-3 text-left text-body",
						option === selected
							? "border-2 border-ansay-500 bg-white"
							: "border-2 border-transparent hover:bg-gray-100",
						optionClassName,
					)}
					data-testid={`pharmacy-picker-sort-option-${option}`}
					onPointerDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
						onSelect(option);
					}}
					onClick={(event) => {
						event.stopPropagation();
						onSelect(option);
					}}
				>
					{t(PHARMACY_PICKER_SORT_LABEL_KEYS[option])}
				</button>
			))}
		</>
	);
}

export function PharmacyPickerSortSidePanel({
	sortContext,
	onClose,
	style,
	docked = false,
}: {
	sortContext: PharmacyPickerSortContext;
	onClose: () => void;
	style?: CSSProperties;
	docked?: boolean;
}) {
	const [sort, setSort] = usePharmacyPickerSort();
	// Figma 4660:59166: no heading — the panel is a plain dropdown of options,
	// positioned by the owning chip 12px underneath it.
	return (
		<aside
			className={cn(
				"pointer-events-auto z-[52] w-[264px] rounded-xl bg-white px-3 pb-3 pt-3 shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]",
				docked ? "absolute" : "fixed",
			)}
			data-testid={PHARMACY_PICKER_SORT_PANEL_TEST_ID}
			style={style}
			onPointerDown={(event) => event.stopPropagation()}
		>
			<div className="flex flex-col gap-2">
				<PharmacyPickerSortOptions
					sortContext={sortContext}
					selected={resolvePharmacyPickerSort(sort, sortContext)}
					onSelect={(option) => {
						setSort(option);
						onClose();
					}}
				/>
			</div>
		</aside>
	);
}
